import React, { useCallback, useRef, useState } from "react";
import axios from "axios";
import "@/App.css";
import {
  FilePdf,
  UploadSimple,
  ArrowClockwise,
  DownloadSimple,
  CheckCircle,
  Warning,
  ArrowsClockwise,
} from "@phosphor-icons/react";
import { toast, Toaster } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SPANISH_WEEKDAY_ORDER = ["LUNES", "MARTES", "MIERCOLES", "JUEVES", "VIERNES", "SABADO", "DOMINGO"];

function PreviewDocument({ data }) {
  if (!data) return null;
  const title = data.month_label ? `${data.title} ${data.month_label}` : data.title;
  return (
    <div
      data-testid="preview-document"
      className="preview-doc animate-paper-drop mx-auto"
      style={{
        width: "100%",
        maxWidth: "820px",
        aspectRatio: "8.5 / 11",
        padding: "24px 28px",
        overflow: "hidden",
        backgroundColor: "#ffffff",
        color: "#000000",
        fontFamily: "Arial, Helvetica, sans-serif",
      }}
    >
      <div className="text-center">
        <h2 className="font-bold" style={{ fontSize: "14pt", letterSpacing: "0" }}>
          {title}
        </h2>
      </div>

      <div
        className="grid grid-cols-2 gap-x-4 mt-2"
        style={{ height: "calc(100% - 32px)", alignItems: "center" }}
      >
        {distributeIntoColumns(data.locations).map((column, ci) => (
          <div key={`col-${ci}`} className="flex flex-col gap-5" style={{ alignSelf: "center" }}>
            {column.map((loc) => (
              <LocationBlock key={loc.name} loc={loc} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function distributeIntoColumns(locations) {
  // Greedy 2-column distribution by estimated row count
  const cols = [[], []];
  const totals = [0, 0];
  for (const loc of locations) {
    const est = (loc.entries || []).reduce(
      (s, e) => s + Math.max(1, (e.names || []).length),
      0
    ) + 4; // header overhead
    const idx = totals[0] <= totals[1] ? 0 : 1;
    cols[idx].push(loc);
    totals[idx] += est;
  }
  return cols;
}

function LocationBlock({ loc }) {
  return (
    <div data-testid="location-block" style={{ fontFamily: "Arial, Helvetica, sans-serif", color: "#000" }}>
      <h3
        className="uppercase"
        style={{ fontSize: "10pt", fontWeight: 700, margin: 0, color: "#2F5496" }}
      >
        {loc.name}
      </h3>
      {loc.address ? (
        <p style={{ fontSize: "8.5pt", margin: 0, lineHeight: "1.15", color: "#8F8F8F" }}>{loc.address}</p>
      ) : null}
      {loc.time ? (
        <p style={{ fontSize: "8.5pt", margin: 0, lineHeight: "1.15", color: "#8F8F8F" }}>{loc.time}</p>
      ) : null}

      <table
        className="w-full mt-1"
        style={{
          fontSize: "10pt",
          borderCollapse: "collapse",
          border: "0.75px solid #000",
          tableLayout: "fixed",
        }}
      >
        <thead>
          <tr>
            <th style={{ width: "22%", border: "0.5px solid #000", padding: "3px 4px", textAlign: "left", fontWeight: 700 }}>DIA</th>
            <th style={{ border: "0.5px solid #000", padding: "3px 4px", textAlign: "left", fontWeight: 700 }}>NOMBRE</th>
            <th style={{ border: "0.5px solid #000", padding: "3px 4px", textAlign: "left", fontWeight: 700 }}>NOMBRE</th>
          </tr>
        </thead>
        <tbody>
          {(loc.entries || []).map((e) => (
            <tr key={e.raw_date}>
              <td
                className="uppercase"
                style={{ border: "0.5px solid #000", padding: "3px 4px", fontWeight: 700, whiteSpace: "nowrap" }}
              >
                {e.day} {e.weekday}
              </td>
              <td style={{ border: "0.5px solid #000", padding: "3px 4px" }}>
                {e.names?.[0] || ""}
              </td>
              <td style={{ border: "0.5px solid #000", padding: "3px 4px" }}>
                {e.names?.[1] || ""}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Converter() {
  const [stage, setStage] = useState("idle"); // idle | uploading | done | error
  const [data, setData] = useState(null);
  const [fileName, setFileName] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Solo se aceptan archivos PDF");
      return;
    }
    setFileName(file.name);
    setStage("uploading");
    setErrorMsg("");
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await axios.post(`${API}/convert`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      setData(res.data);
      setStage("done");
      toast.success("Documento convertido");
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        "Error desconocido procesando el PDF";
      setErrorMsg(typeof msg === "string" ? msg : "Error procesando el PDF");
      setStage("error");
      toast.error("No se pudo convertir el PDF");
    }
  }, []);

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) handleFile(f);
  };

  const onPick = () => inputRef.current?.click();

  const reset = () => {
    setStage("idle");
    setData(null);
    setFileName("");
    setErrorMsg("");
    if (inputRef.current) inputRef.current.value = "";
  };

  const onDownload = async () => {
    if (!data?.id) return;
    try {
      const res = await axios.get(`${API}/download/${data.id}`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `arreglos-${(data.month_label || "doc").toLowerCase().replace(/\s+/g, "-")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("No se pudo descargar el archivo");
    }
  };

  return (
    <div className="bg-paper-grain min-h-screen w-full">
      <Toaster position="top-center" theme="light" richColors />
      <div className="max-w-5xl mx-auto px-6 py-10 md:py-16">
        {/* Header */}
        <header className="flex items-center justify-between mb-12 animate-fade-up">
          <div className="flex items-center gap-3" data-testid="brand">
            <div className="w-9 h-9 flex items-center justify-center bg-[#1A1A1A] text-white">
              <FilePdf size={20} weight="duotone" />
            </div>
            <div className="leading-tight">
              <div className="font-serif-display text-xl tracking-tight">Folio</div>
              <div className="label-eyebrow">PDF · One Page</div>
            </div>
          </div>
          <div className="hidden sm:block label-eyebrow">v1.0 · 2026</div>
        </header>

        {/* Hero */}
        <section className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-10 animate-fade-up delay-1">
          <div className="md:col-span-8">
            <p className="label-eyebrow mb-3">Convertidor de Asignaciones</p>
            <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.95] text-[#1A1A1A]">
              Convierte tu PDF de varias páginas en
              <span className="italic text-[#C04A33]"> una sola hoja</span>.
            </h1>
          </div>
          <div className="md:col-span-4 md:text-right">
            <p className="text-base leading-relaxed text-[#6E6D68]">
              Sube el reporte multipágina y obtén un documento compacto, alineado al formato clásico
              de arreglos públicos — listo para imprimir.
            </p>
          </div>
        </section>

        <hr className="dotted-rule mb-10" />

        {/* Stage area */}
        {stage === "idle" || stage === "error" ? (
          <div className="animate-fade-up delay-2" data-testid="stage-idle">
            <div
              role="button"
              tabIndex={0}
              data-testid="upload-dropzone"
              onClick={onPick}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onPick()}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              className={`border-2 border-dashed border-[#E6E4DD] bg-white/50 px-8 py-16 sm:py-24 cursor-pointer text-center transition-all duration-300 ease-out ${
                dragOver ? "dropzone-active" : "hover:bg-white"
              }`}
            >
              <div className="flex flex-col items-center gap-4">
                <div className="w-14 h-14 flex items-center justify-center bg-[#F8F7F4] border border-[#E6E4DD]">
                  <UploadSimple size={26} weight="regular" className="text-[#1A1A1A]" />
                </div>
                <div className="font-serif-display text-2xl sm:text-3xl text-[#1A1A1A]">
                  Arrastra tu PDF aquí
                </div>
                <p className="text-sm text-[#6E6D68]">
                  o haz click para seleccionar — solo archivos <strong>.pdf</strong>
                </p>
                <button
                  type="button"
                  data-testid="upload-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onPick();
                  }}
                  className="btn-terracotta px-6 py-3 text-sm uppercase tracking-[0.18em] font-semibold mt-2"
                >
                  Seleccionar archivo
                </button>
                <input
                  ref={inputRef}
                  type="file"
                  accept="application/pdf,.pdf"
                  className="hidden"
                  data-testid="upload-input"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                />
              </div>
            </div>

            {stage === "error" && (
              <div
                data-testid="error-banner"
                className="mt-6 flex items-start gap-3 p-4 bg-white border border-[#E6E4DD]"
              >
                <Warning size={20} weight="fill" className="text-[#C04A33] mt-0.5" />
                <div>
                  <div className="font-semibold text-[#1A1A1A]">No se pudo procesar el archivo</div>
                  <div className="text-sm text-[#6E6D68]">{errorMsg}</div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-12">
              {[
                { n: "01", t: "Sube", d: "PDF de varias páginas con tablas por ubicación." },
                { n: "02", t: "Procesa", d: "Extraemos fechas, horarios y publicadores." },
                { n: "03", t: "Descarga", d: "Documento de una sola página, listo." },
              ].map((s) => (
                <div key={s.n} className="border-t border-[#E6E4DD] pt-4">
                  <div className="label-eyebrow">{s.n}</div>
                  <div className="font-serif-display text-2xl mt-1">{s.t}</div>
                  <p className="text-sm text-[#6E6D68] mt-1 leading-relaxed">{s.d}</p>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {stage === "uploading" && (
          <div
            data-testid="stage-processing"
            className="bg-white border border-[#E6E4DD] p-12 text-center animate-fade-up"
          >
            <div className="inline-flex items-center justify-center w-14 h-14 bg-[#F8F7F4] border border-[#E6E4DD] mb-4">
              <ArrowsClockwise size={26} className="text-[#C04A33] animate-spin" />
            </div>
            <div className="font-serif-display text-3xl text-[#1A1A1A]">Procesando documento</div>
            <p className="text-sm text-[#6E6D68] mt-2">
              Leyendo <span className="font-semibold text-[#1A1A1A]">{fileName}</span> y
              componiendo la hoja final…
            </p>
          </div>
        )}

        {stage === "done" && data && (
          <div data-testid="stage-result" className="animate-fade-up">
            {/* Action bar */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              <div className="flex items-start gap-3">
                <CheckCircle size={22} weight="fill" className="text-[#C04A33] mt-0.5" />
                <div>
                  <div className="font-semibold text-[#1A1A1A]">Documento listo</div>
                  <div className="text-sm text-[#6E6D68]">
                    {data.locations?.length || 0} ubicaciones · {fileName}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  data-testid="reset-button"
                  onClick={reset}
                  className="btn-ghost-ink px-4 py-3 text-xs uppercase tracking-[0.18em] font-semibold flex items-center gap-2 border border-[#E6E4DD]"
                >
                  <ArrowClockwise size={16} /> Otro PDF
                </button>
                <button
                  type="button"
                  data-testid="download-button"
                  onClick={onDownload}
                  className="btn-terracotta px-5 py-3 text-xs uppercase tracking-[0.18em] font-semibold flex items-center gap-2"
                >
                  <DownloadSimple size={16} weight="bold" /> Descargar PDF
                </button>
              </div>
            </div>

            <div className="bg-[#EFEDE6] p-6 sm:p-10 border border-[#E6E4DD]">
              <PreviewDocument data={data} />
            </div>
          </div>
        )}

        {/* Footer */}
        <footer className="mt-16 pt-6 border-t border-[#E6E4DD] flex items-center justify-between">
          <div className="label-eyebrow">Folio · Convertidor</div>
          <div className="text-xs text-[#6E6D68]">
            Hecho con cuidado · 100% local
          </div>
        </footer>
      </div>
    </div>
  );
}

function App() {
  return <Converter />;
}

export default App;
// Avoid unused warnings if helpers exist for future use
export { SPANISH_WEEKDAY_ORDER };
